var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import SafeEventEmitter from "@metamask/safe-event-emitter";
import { ADAPTER_EVENTS, ADAPTER_STATUS, checkChainId, log, SUPPORTED_ADAPTERS, } from "../base";
import { clearState, loadState, LocalStorageKeys, saveState, } from "../base/utils";
export const PROTICO_AUTH_EVENTS = Object.assign(Object.assign({}, ADAPTER_STATUS), { CHAIN_CHANGED: "chain_changed", ACCOUNT_CHANGED: "account_changed" });
export class ProticoAuthCore extends SafeEventEmitter {
    constructor(options) {
        super();
        this.connectedAdapterName = null;
        this.status = ADAPTER_STATUS.NOT_READY;
        this.cachedAdapter = null;
        this.cachedGoogleLoginAddress = null;
        this.walletAdapters = {};
        if (options.enableLogging)
            log.enableAll();
        else
            log.disableAll();
        this.cachedAdapter = loadState(LocalStorageKeys.ADAPTER_CACHE_KEY);
        this.cachedGoogleLoginAddress = loadState(LocalStorageKeys.GOOGLE_LOGIN_ADDRESS);
        if (!checkChainId(options.chainId)) {
            throw Error("Invalid chainId");
        }
        this.coreOptions = options;
        this.subscribeToAdapterEvents = this.subscribeToAdapterEvents.bind(this);
        this.postConnected = this.postConnected.bind(this);
        this.postDisconnected = this.postDisconnected.bind(this);
    }
    get provider() {
        if (this.status === ADAPTER_STATUS.CONNECTED &&
            this.connectedAdapterName &&
            (this.connectedAdapterName !== SUPPORTED_ADAPTERS.GUEST && this.connectedAdapterName !== SUPPORTED_ADAPTERS.GOOGLE)) {
            const adapter = this.walletAdapters[this.connectedAdapterName];
            return adapter.provider;
        }
        return null;
    }
    init(useGuestFallback, address) {
        return __awaiter(this, void 0, void 0, function* () {
            // Configure adapters if provided in coreOptions
            if (this.coreOptions.adapterConfigs) {
                const adapterNames = Object.keys(this.coreOptions.adapterConfigs);
                for (const adapterName of adapterNames) {
                    if (!this.coreOptions.adapterConfigs[adapterName].enable)
                        continue;
                    const adapter = yield createAdapterInstance(adapterName, this.coreOptions.adapterConfigs, this.coreOptions.web3AccessTokenServer);
                    this.configureAdapter(adapter);
                }
            }
            const initPromises = Object.keys(this.walletAdapters).map((adapterName) => {
                this.subscribeToAdapterEvents(this.walletAdapters[adapterName]);
                const providedChainId = this.coreOptions.chainId;
                this.walletAdapters[adapterName].setAdapterSettings({
                    chainId: providedChainId,
                });
                return this.walletAdapters[adapterName]
                    .init({ autoConnect: this.cachedAdapter === adapterName })
                    .catch((e) => log.error(e));
            });
            this.status = ADAPTER_STATUS.READY;
            yield Promise.all(initPromises);
            if (useGuestFallback &&
                (this.cachedAdapter === SUPPORTED_ADAPTERS.GUEST || !this.cachedAdapter)) {
                yield this.connectTo(SUPPORTED_ADAPTERS.GUEST);
            }
            if (this.cachedAdapter === SUPPORTED_ADAPTERS.GOOGLE) {
                yield this.connectTo(SUPPORTED_ADAPTERS.GOOGLE, this.cachedGoogleLoginAddress);
            }
        });
    }
    configureAdapter(adapter) {
        this.checkInitRequirements();
        this.walletAdapters[adapter.name] = adapter;
    }
    clearCache() {
        clearState(LocalStorageKeys.ADAPTER_CACHE_KEY);
        clearState(LocalStorageKeys.GOOGLE_LOGIN_ADDRESS);
        this.cachedAdapter = null;
    }
    /**
     * Connect to a specific wallet adapter
     * @param walletName - Key of the walletAdapter to use.
     */
    connectTo(walletName, address) {
        return __awaiter(this, void 0, void 0, function* () {
            if (walletName === SUPPORTED_ADAPTERS.GUEST) {
                yield this.postConnected({ adapter: SUPPORTED_ADAPTERS.GUEST });
                return null;
            }
            if (walletName === SUPPORTED_ADAPTERS.GOOGLE) {
                yield this.postConnected({ adapter: SUPPORTED_ADAPTERS.GOOGLE, status: {
                        address,
                    } });
                return null;
            }
            if (!this.walletAdapters[walletName])
                throw Error(`Please add wallet adapter for ${walletName} wallet, before connecting`);
            if (this.status === ADAPTER_STATUS.CONNECTED) {
                yield this.disconnect();
            }
            const provider = yield this.walletAdapters[walletName].connect();
            return provider;
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status !== ADAPTER_STATUS.CONNECTED || !this.connectedAdapterName)
                throw Error(`No wallet is connected`);
            if (this.connectedAdapterName === SUPPORTED_ADAPTERS.GUEST) {
                this.postDisconnected();
                return;
            }
            if (this.connectedAdapterName === SUPPORTED_ADAPTERS.GOOGLE) {
                this.postDisconnected();
                return;
            }
            if (this.connectedAdapterName === SUPPORTED_ADAPTERS.METAMASK) {
                yield this.walletAdapters[this.connectedAdapterName].provider.request({
                    method: 'wallet_revokePermissions',
                    params: [
                        {
                            eth_accounts: {},
                        },
                    ],
                });
            }
            yield this.walletAdapters[this.connectedAdapterName].disconnect();
        });
    }
    switchChain(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status !== ADAPTER_STATUS.CONNECTED || !this.connectedAdapterName)
                throw Error(`No wallet is connected`);
            return this.walletAdapters[this.connectedAdapterName].switchChain(chainId);
        });
    }
    testPersonalSign() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.provider)
                throw Error("No provider connected");
            try {
                const account = yield this.walletAdapters[this.connectedAdapterName].ensureConnectedAndGetAccount();
                const signedMessage = (yield this.provider.request({
                    method: "personal_sign",
                    params: ["Hello world", account],
                }));
                return signedMessage;
            }
            catch (e) {
                throw Error("Error verifying signed challenge");
            }
        });
    }
    getConnectedAccount() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connectedAdapterName === SUPPORTED_ADAPTERS.GOOGLE) {
                return this.cachedGoogleLoginAddress;
            }
            if (!this.provider)
                throw Error("No provider connected");
            return this.walletAdapters[this.connectedAdapterName].ensureConnectedAndGetAccount();
        });
    }
    postConnected(data) {
        return __awaiter(this, void 0, void 0, function* () {
            this.status = ADAPTER_STATUS.CONNECTED;
            this.connectedAdapterName = data.adapter;
            if (this.connectedAdapterName == SUPPORTED_ADAPTERS.GOOGLE) {
                this.cacheWallet(data.adapter, data.status.address);
            }
            else {
                this.cacheWallet(data.adapter);
            }
            this.emit(PROTICO_AUTH_EVENTS.CONNECTED, data);
            log.debug(`proticoauth send connected, status: ${this.status}, adapter: ${this.connectedAdapterName}`);
        });
    }
    postDisconnected() {
        // get back to ready state for rehydrating.
        this.status = ADAPTER_STATUS.READY;
        const cachedAdapter = loadState(LocalStorageKeys.ADAPTER_CACHE_KEY);
        if (this.connectedAdapterName === cachedAdapter) {
            this.clearCache();
        }
        log.debug(`disconnected, status: ${this.status}, adapter: ${this.connectedAdapterName}`);
        this.connectedAdapterName = null;
        this.emit(PROTICO_AUTH_EVENTS.DISCONNECTED);
    }
    subscribeToAdapterEvents(walletAdapter) {
        walletAdapter.on(ADAPTER_EVENTS.CONNECTED, this.postConnected);
        walletAdapter.on(ADAPTER_EVENTS.DISCONNECTED, this.postDisconnected);
        walletAdapter.on(ADAPTER_EVENTS.CONNECTING, (data) => {
            this.status = ADAPTER_STATUS.CONNECTING;
            this.emit(PROTICO_AUTH_EVENTS.CONNECTING, data.adapter);
            log.debug(`connecting, status: ${this.status}, adapter: ${data.adapter}`);
            setTimeout(() => {
                if (this.status === ADAPTER_STATUS.CONNECTING &&
                    this.cachedAdapter === SUPPORTED_ADAPTERS.OPENLOGIN) {
                    this.status = ADAPTER_STATUS.ERRORED;
                    this.emit(PROTICO_AUTH_EVENTS.ERRORED, {
                        adapter: data.adapter,
                        message: "Connection timed out",
                    });
                    log.debug(`errored, status: ${this.status}, adapter: ${this.connectedAdapterName}`);
                    if (!this.connectedAdapterName) {
                        // if errored before connecting to any adapter, try connecting to guest adapter
                        this.connectTo(SUPPORTED_ADAPTERS.GUEST);
                    }
                }
            }, 5000);
        });
        walletAdapter.on(ADAPTER_EVENTS.ERRORED, (data) => {
            this.status = ADAPTER_STATUS.ERRORED;
            this.clearCache();
            this.emit(PROTICO_AUTH_EVENTS.ERRORED, data);
            log.debug(`errored, status: ${this.status}, adapter: ${this.connectedAdapterName}`);
            if (!this.connectedAdapterName) {
                // if errored before connecting to any adapter, try connecting to guest adapter
                this.connectTo(SUPPORTED_ADAPTERS.GUEST);
            }
        });
        walletAdapter.on(ADAPTER_EVENTS.CHAIN_CHANGED, (data) => {
            log.debug(`core adapter chain changed: ${data}`);
            this.emit(PROTICO_AUTH_EVENTS.CHAIN_CHANGED, data);
        });
        walletAdapter.on(ADAPTER_EVENTS.ACCOUNT_CHANGED, (data) => {
            log.debug(`core adapter account changed: ${JSON.stringify(data)}`);
            this.emit(PROTICO_AUTH_EVENTS.ACCOUNT_CHANGED, data);
        });
    }
    checkInitRequirements() {
        if (this.status === ADAPTER_STATUS.CONNECTING)
            throw Error("Already pending connection");
        if (this.status === ADAPTER_STATUS.CONNECTED)
            throw Error("Already connected");
        if (this.status === ADAPTER_STATUS.READY)
            throw Error("Adapter is already initialized");
    }
    cacheWallet(walletName, address) {
        saveState(LocalStorageKeys.ADAPTER_CACHE_KEY, walletName);
        if (walletName === SUPPORTED_ADAPTERS.GOOGLE) {
            saveState(LocalStorageKeys.GOOGLE_LOGIN_ADDRESS, address.toLowerCase());
        }
        this.cachedAdapter = walletName;
        this.cachedGoogleLoginAddress = address;
    }
}
function createAdapterInstance(adapterName, adapterConfig, web3AccessTokenServer) {
    return __awaiter(this, void 0, void 0, function* () {
        const adapterConfigValue = Object.assign(Object.assign({}, adapterConfig[adapterName]), { web3AccessTokenServer });
        switch (adapterName) {
            case SUPPORTED_ADAPTERS.METAMASK:
                const metamaskAdapterModule = yield import("../adapters/metamaskAdapter");
                return new metamaskAdapterModule.MetamaskAdapter(adapterConfigValue);
            case SUPPORTED_ADAPTERS.OPENLOGIN:
                const openloginAdapterModule = yield import("../adapters/openloginAdapter");
                return new openloginAdapterModule.OpenloginAdapter(adapterConfigValue);
            case SUPPORTED_ADAPTERS.WALLET_CONNECT_V2:
                const module = yield import("../adapters/walletConnectV2Adapter");
                return new module.WalletConnectV2Adapter(adapterConfigValue);
            // Add other cases for other supported adapters
            default:
                throw new Error(`Unsupported adapter: ${adapterName}`);
        }
    });
}
//# sourceMappingURL=index.js.map